import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/app/hooks';
import Avatar from 'src/components/ui/Avatar/Avatar';
import Button from 'src/components/ui/Button/Button';
import Input from 'src/components/ui/Input/Input';
import ManufacturersTable from 'src/components/ui/ManufacturersTable/ManufacturersTable';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import { ThemeSwitch } from 'src/components/ui/ThemeSwitch/ThemeSwitch';
import UploadPicture from 'src/components/ui/UploadPicture/UploadPicture';
import { changeSettings } from 'src/store/actions/authActions';
import { useFileSelector } from 'src/utils/useFileSelector';
import { v4 as uuidv4 } from 'uuid';

import { addModal } from '../../../../features/globalSlice';
import { ModalType } from '../../../../store/models/Modal';
import classes from './ServiceSettings.module.scss';

const ServiceSettings: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const imageId = useRef(uuidv4());

  const image = useFileSelector({ id: imageId.current })?.[0];
  const user = useAppSelector((state) => state.auth.user);

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('common:settings')}
        endNode={
          <div className={classes.HeaderRight}>
            <ThemeSwitch />
            <Button
              disabled={!image}
              onClick={() => dispatch(changeSettings({ image: image }))}
              className={classes.HeaderButton}
            >
              {t('common:save_changes')}
            </Button>
          </div>
        }
      />
      <h4>{t('settings:profile_picture')}</h4>
      <section>
        <UploadPicture
          imageComponent={
            <Avatar
              image={image ? image?.url : user?.image}
              imageSize={128}
              title={user?.title}
              showTitles={false}
            />
          }
          imageId={imageId.current}
          enableRemove={!!(image?.url || (user?.image && image?.url !== null))}
          className={classes.Picture}
        />
      </section>
      <h4>{t('settings:personal_info')}</h4>
      <section>
        <p>{t('settings:change_personal_info_warning')}</p>
        <div>
          <Input
            value={user?.title}
            label={t('common:name')}
            placeholder={t('common:name')}
            disabled
          />
          <Input
            value={user?.eMail}
            label={t('common:email')}
            placeholder={t('common:email')}
            disabled
          />
          <div />
          <Button
            size="large"
            variant="outlined"
            onClick={() =>
              dispatch(
                addModal({
                  id: '1',
                  title: t('settings:change_password'),
                  modalType: ModalType.CHANGE_PASSWORD,
                  primaryButtonText: t('common:confirm'),
                  secondaryButtonText: t('common:cancel'),
                  onBackdropClose: false,
                })
              )
            }
          >
            {t('settings:change_password')}
          </Button>
        </div>
      </section>
      <h4>{t('settings:my_accounts')}</h4>
      <section>
        <ManufacturersTable />
      </section>
    </div>
  );
};

export default ServiceSettings;
